import { createContext, Suspense, useContext, useEffect, useState } from 'react';
import {
  ConfirmationResult,
  onAuthStateChanged,
  RecaptchaVerifier,
  sendPasswordResetEmail,
  signInWithPhoneNumber,
  signOut,
  User
} from '@firebase/auth';
import { auth } from './firebase';
import { getLoggedUserPermissions } from '../services/configuration.service';
import { getUserInfoData } from '../utils/getNudosData';
import { LoadingOutlined } from '@ant-design/icons';
import useOnlyDevelopers from './setOnlyDevelopers';
import { displayErrorNotification, displaySuccessNotification } from '../utils/displayNudosStandardNotifications';
import { getAllUsers } from '../services/users';
import useUsersState from './useUsersState';

const userAuthContext = createContext<{
  user?: User | null;
  setUpRecaptha?: (number: string) => Promise<ConfirmationResult>;
  logOut?: () => void;
  resetPassword?: (email: string) => void;
}>({});

export function UserAuthContextProvider({ children }: { children: JSX.Element }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingLS, setLoadingST] = useState<boolean>(false);
  const { setOnlyDevelopers } = useOnlyDevelopers();
  const { setUsers } = useUsersState();

  const nudosInfo = getUserInfoData();
  function setUpRecaptha(number: string) {
    const recaptchaVerifier = new RecaptchaVerifier(
      'sign-button',
      {
        size: 'invisible',
        callback: () => undefined
      },
      auth
    );
    recaptchaVerifier.render();
    return signInWithPhoneNumber(auth, number, recaptchaVerifier);
  }

  function logOut() {
    signOut(auth)
      .then(() => {
        localStorage.removeItem('userNudosData');
        localStorage.removeItem('_userInfo');
        window.location.replace('/auth/login');
      })
      .catch(() => displayErrorNotification());
  }

  const userInfo = async () => {
    setLoading(true);
    try {
      if (nudosInfo && nudosInfo?.uuid) {
        const nudosData = await getLoggedUserPermissions(nudosInfo.uuid);
        localStorage.setItem('userNudosData', JSON.stringify(nudosData));
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      localStorage.removeItem('userNudosData');
      localStorage.removeItem('_userInfo');
      localStorage.removeItem('onlyDevs');
      window.location.href = '/auth/login';
    }
  };

  const handleGetAllUsers = async () => {
    try {
      const allUsers = await getAllUsers();
      if (allUsers) setUsers(allUsers);
    } catch (error) {
      displayErrorNotification();
    }
  };

  const searchOnlyDevelopers = () => {
    setLoadingST(true);
    if (localStorage.getItem('onlyDevs')) {
      const isTrueOnlyDevs = localStorage.getItem('onlyDevs') === 'true';
      if (isTrueOnlyDevs) {
        setOnlyDevelopers(true);
      } else {
        setOnlyDevelopers(false);
      }
      setLoadingST(false);
    } else {
      setOnlyDevelopers(false);
      setLoadingST(false);
    }
  };

  const resetPassword = (email: string) => {
    sendPasswordResetEmail(auth, email.trim())
      .then(() =>
        displaySuccessNotification({
          customJSXMessage: (
            <>
              Se ha enviado al correo {email || ''} un enlace para que actualices tu contraseña. Si no lo ves en bandeja
              de entrada revisa la carpeta de spam.
            </>
          )
        })
      )
      .catch(error => {
        displayErrorNotification();
      });
  };

  useEffect(() => {
    searchOnlyDevelopers();
    const unsubscribe = onAuthStateChanged(auth, async currentuser => {
      await userInfo();
      setUser(currentuser);
      if (currentuser) handleGetAllUsers();
    });

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Suspense fallback="LOADING">
      <userAuthContext.Provider
        value={{
          user,
          setUpRecaptha,
          logOut,
          resetPassword
        }}
      >
        {loading || loadingLS ? (
          <div className="w-screen h-screen flex items-center content-center justify-center">
            <LoadingOutlined style={{ color: '#070F21', fontSize: '50px' }} />
          </div>
        ) : (
          children
        )}
      </userAuthContext.Provider>
    </Suspense>
  );
}
export function useUserAuth() {
  return useContext(userAuthContext);
}
